import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertCircle, Info } from 'lucide-react';
interface TooltipItem {
  name: string;
  badge: string;
  badgeColor: string;
  bgColor: string;
  borderColor: string;
  tooltip: string;
}
export function IngredientDemo() {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const avoidItems: TooltipItem[] = [{
    name: 'Red 40, Yellow 5',
    badge: 'AVOID',
    badgeColor: 'bg-red-100 text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-100',
    tooltip: 'Artificial colors linked to hyperactivity in children and potential carcinogenic effects. Banned in several European countries.'
  }, {
    name: 'Sodium Benzoate',
    badge: 'AVOID',
    badgeColor: 'bg-red-100 text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-100',
    tooltip: 'A preservative that can form benzene (a carcinogen) when combined with vitamin C. May trigger allergic reactions.'
  }];
  const limitItems: TooltipItem[] = [{
    name: 'Sugar, Corn Syrup',
    badge: 'LIMIT',
    badgeColor: 'bg-accent-100 text-accent-700',
    bgColor: 'bg-accent-50',
    borderColor: 'border-accent-100',
    tooltip: 'Excessive sugar intake is linked to obesity, type 2 diabetes, and heart disease. Corn syrup is a highly processed sweetener.'
  }];
  const safeItems: TooltipItem[] = [{
    name: 'Water, Citric Acid, Natural Flavors',
    badge: 'SAFE',
    badgeColor: 'bg-brand-100 text-brand-700',
    bgColor: 'bg-brand-50',
    borderColor: 'border-brand-100',
    tooltip: 'These are generally recognized as safe (GRAS). Citric acid is naturally found in citrus fruits. Natural flavors are derived from plant or animal sources.'
  }];
  const allItems = [...avoidItems, ...limitItems, ...safeItems];
  return <section className="py-20 bg-white overflow-hidden" data-id="element-1426" data-mp="3kxupta3bzn9p">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1427" data-mp="3ik7nypuk0lqi">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1428" data-mp="3ihmkjwelxxqn">
          {/* Left: Text */}
          <div className="order-2 lg:order-1" data-id="element-1429" data-mp="3ekqi3vb7tmkt">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-1430" data-mp="39kc6y4ebhwik">
              See It In Action
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-1431" data-mp="34m0x3to0vm0c">
              IntelliEats breaks down complex ingredient lists into simple,
              actionable insights. Hover over each result to learn why it
              matters.
            </p>

            <div className="space-y-6" data-id="element-1432" data-mp="3shdcprfp40nk">
              <div className="flex items-start gap-4" data-id="element-1433" data-mp="3sgaqvxx8coei">
                <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0" data-id="element-1434" data-mp="3nju1l0eao3zu">
                  <Check className="w-5 h-5 text-brand-600" data-id="element-1435" data-mp="ou3kb3hoh2pk" />
                </div>
                <div data-id="element-1436" data-mp="k22270xtwhrm">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-1437" data-mp="k1jyjj8u719o">
                    Safe Ingredients
                  </h3>
                  <p className="text-slate-600" data-id="element-1438" data-mp="rfkv5jv1ah1g">
                    Green indicators for natural, healthy ingredients that are
                    safe to consume.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-1439" data-mp="16ahaeh19g4o1">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0" data-id="element-1440" data-mp="16c2464fr3jku">
                  <AlertCircle className="w-5 h-5 text-accent-600" data-id="element-1441" data-mp="1aa9k4x2e4guz" />
                </div>
                <div data-id="element-1442" data-mp="1f9m9bogqi00q">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-1443" data-mp="1k25k6do1eh1u">
                    Limited Intake
                  </h3>
                  <p className="text-slate-600" data-id="element-1444" data-mp="1p1rw8js4pr92">
                    Amber warnings for ingredients like sugar or sodium that
                    should be moderated.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-1445" data-mp="1y2q0n36hc0s6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0" data-id="element-1446" data-mp="2vqjn406c48pp">
                  <X className="w-5 h-5 text-red-600" data-id="element-1447" data-mp="2qs8chn87tu6f" />
                </div>
                <div data-id="element-1448" data-mp="34j3m51qjgfb2">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-1449" data-mp="34jmxfzloo5g4">
                    Ingredients to Avoid
                  </h3>
                  <p className="text-slate-600" data-id="element-1450" data-mp="30i1nam0ja59m">
                    Red alerts for harmful additives, artificial colors, and
                    preservatives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Visual Demo */}
          <div className="order-1 lg:order-2 relative" data-id="element-1451" data-mp="2ltevhktpzl1i">
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-100 to-accent-100 rounded-3xl transform rotate-3 scale-105 -z-10 opacity-50" data-id="element-1452" data-mp="2gtshh962i0rn"></div>

            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden" data-id="element-1453" data-mp="10jtvz7v19g9">
              {/* Mock Header */}
              <div className="bg-slate-50 border-b border-slate-100 p-4 flex items-center gap-2" data-id="element-1454" data-mp="4kk94mhez898">
                <div className="flex gap-1.5" data-id="element-1455" data-mp="8lnpda3dj6az">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-1456" data-mp="8ngxkttmxyl3"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-1457" data-mp="3qsbugzyfbtgn"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-1458" data-mp="3vqdblfcahiph"></div>
                </div>
                <div className="ml-4 text-xs font-medium text-slate-400" data-id="element-1459" data-mp="natoyz64iyje">
                  Analysis Result
                </div>
                <div className="ml-auto flex items-center gap-1 text-xs text-slate-400" data-id="element-1460" data-mp="sa6b1hp681kx">
                  <Info className="w-3 h-3" data-id="element-1461" data-mp="dls7f4jxk93b" />
                  Hover for details
                </div>
              </div>

              <div className="p-6 sm:p-8" data-id="element-1462" data-mp="iiscbc51n5su">
                <div className="mb-6" data-id="element-1463" data-mp="ihqt2kdvjd01">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1464" data-mp="168mpjut9ywdm">
                    Scanned Text
                  </h4>
                  <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-600 font-mono leading-relaxed border border-slate-100" data-id="element-1465" data-mp="1b27gj2wgaq3b">
                    Water, Sugar, Corn Syrup, Artificial Colors (Red 40, Yellow
                    5), Sodium Benzoate, Citric Acid, Natural Flavors
                  </div>
                </div>

                <div data-id="element-1466" data-mp="16x4zr4wn8hvy">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1467" data-mp="16vtsgv3zos03">
                    AI Detection
                  </h4>
                  <div className="space-y-2" data-id="element-1468" data-mp="x6sfzewnksqv">
                    {allItems.map((item, index) => <motion.div key={item.name} initial={{
                    opacity: 0,
                    x: -10
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: index * 0.15
                  }} className={`relative flex items-center justify-between p-3 ${item.bgColor} border ${item.borderColor} rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`} onMouseEnter={() => setHoveredItem(item.name)} onMouseLeave={() => setHoveredItem(null)} data-id="element-1469" data-mp="s7ft4u5s7loa" data-mpi={index}>
                        <span className="font-medium text-slate-900" data-id="element-1470" data-mp="2b40xs9vzeunw">
                          {item.name}
                        </span>
                        <span className={`px-2 py-1 ${item.badgeColor} text-xs font-bold rounded shrink-0 ml-2`} data-id="element-1471" data-mp="2kwgfr77a3j8s">
                          {item.badge}
                        </span>

                        {/* Tooltip */}
                        <AnimatePresence data-id="element-1472" data-mp="lsjrl3mnyf0g">
                          {hoveredItem === item.name && <motion.div initial={{
                        opacity: 0,
                        y: 8,
                        scale: 0.95
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 8,
                        scale: 0.95
                      }} transition={{
                        duration: 0.2
                      }} className="absolute left-0 right-0 top-full mt-2 z-30 bg-slate-900 text-white text-xs rounded-xl p-3 shadow-xl leading-relaxed" data-id="element-1473" data-mp="gtqevdf7as1c">
                              <div className="absolute -top-1.5 left-6 w-3 h-3 bg-slate-900 transform rotate-45" data-id="element-1474" data-mp="2255h4vylkm00"></div>
                              {item.tooltip}
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}